import { type ReactNode } from 'react';

import { type BaseCustomField } from '@air/api/types';
import {
  Ai,
  Air,
  Calendar,
  Camera,
  Clips,
  Copyright,
  Fields,
  Folder,
  FolderOpen,
  Gear,
  ImageSearchIcon,
  Import,
  Key,
  Location,
  LocationsIcon,
  Palette,
  People,
  Tag,
  User,
  Video,
} from '@air/next-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { type SelectedFilter } from '~/store/filters/types';
import { type IconType } from '~/types/icons';

export const AllDateFilters = ['dateCreated', 'dateUploaded', 'dateModified', 'dateTaken'] as const;
export type DateFilters = (typeof AllDateFilters)[number];

export const AllAudioVisualFilters = ['videoFrameRate', 'videoAspectRatio', 'audioCoding', 'audioSampleRate'] as const;
export type AudioVisualFilters = (typeof AllAudioVisualFilters)[number];

export const AllLocationFilters = ['country', 'city', 'state'] as const;
export type LocationFilters = (typeof AllLocationFilters)[number];

export const AllAdvancedFilters = ['camera', 'creator', 'copyright', 'importedKeywords'] as const;
export type AdvancedFilters = (typeof AllAdvancedFilters)[number];

export type FilterType =
  | AdvancedFilters
  | AudioVisualFilters
  | DateFilters
  | LocationFilters
  | 'audioVisual'
  | 'orientation'
  | 'color'
  | 'colorThreshold'
  | 'customField'
  | 'date'
  | 'location'
  | 'extension'
  | 'advanced'
  | 'other'
  | 'people'
  | 'smartTags'
  | 'source'
  | 'tags'
  | 'type'
  | 'assetFileKind'
  | 'uploader'
  | 'locations'
  | 'separator1'
  | 'separator2'
  | 'similarToImage'
  | 'similarTo';

const filtersInOrder: FilterType[] = [
  'locations',
  'type',
  'assetFileKind',
  'tags',
  'smartTags',
  'separator1',
  'customField',
  'uploader',
  'source',
  'color',
  'people',
  'camera',
  'creator',
  'copyright',
  'importedKeywords',
  'extension',
  'location',
  'city',
  'state',
  'country',
  'orientation',
  'audioVisual',
  'audioCoding',
  'audioSampleRate',
  'videoAspectRatio',
  'videoFrameRate',
  'date',
  'dateCreated',
  'dateModified',
  'dateTaken',
  'dateUploaded',
  'separator2',
  'other',
];

export const IPTC_FIELDS: FilterType[] = [
  'dateTaken',
  'camera',
  'importedKeywords',
  'copyright',
  'creator',
  'audioVisual',
  'videoFrameRate',
  'videoAspectRatio',
  'audioCoding',
  'audioSampleRate',
  'location',
  'city',
  'state',
  'country',
];

export const filtersOrder = filtersInOrder.reduce<Partial<Record<FilterType, number>>>((acc, filter, index) => {
  acc[filter] = index;
  return acc;
}, {});

export const sortFiltersByOrder = (filterA: FilterType, filterB: FilterType) =>
  (filtersOrder[filterA] ?? 0) - (filtersOrder[filterB] ?? 0);

export type FilterBaseMenuOption = {
  id: FilterType;
  subOptions?: (DropdownMenuOption & { id: FilterType | string; 'data-testid'?: string; onClick?: () => void })[];
  label: string | ReactNode;
  icon?: IconType;
};

export type CustomFieldFilterType = Pick<BaseCustomField, 'id' | 'name' | 'type'>;

export const baseFiltersOptions: Record<FilterType, FilterBaseMenuOption> = {
  orientation: { id: 'orientation', label: 'Orientation', icon: Video },
  audioCoding: { id: 'audioCoding', label: 'Audio Coding', icon: Video },
  audioSampleRate: { id: 'audioSampleRate', label: 'Audio Sample Rate', icon: Video },
  audioVisual: { id: 'audioVisual', label: 'Audio/Visual', icon: Clips },
  camera: { id: 'camera', label: 'Camera', icon: Camera },
  city: { id: 'city', label: 'City', icon: Location },
  color: { id: 'color', label: 'Color', icon: Palette },
  copyright: { id: 'copyright', label: 'Copyright', icon: Copyright },
  country: { id: 'country', label: 'Country', icon: Location },
  creator: { id: 'creator', label: 'Creator', icon: User },
  customField: { id: 'customField', label: 'Custom Fields', icon: Fields },
  date: { id: 'date', label: 'Date', icon: Calendar },
  dateCreated: { id: 'dateCreated', label: 'Date Created', icon: Calendar },
  dateModified: { id: 'dateModified', label: 'Date Modified', icon: Calendar },
  dateTaken: { id: 'dateTaken', label: 'Date Taken', icon: Calendar },
  dateUploaded: { id: 'dateUploaded', label: 'Date Uploaded', icon: Calendar },
  extension: { id: 'extension', label: 'File Extension', icon: Folder },
  importedKeywords: { id: 'importedKeywords', label: 'Imported Keywords', icon: Key },
  location: { id: 'location', label: 'Embedded Location', icon: Location },
  other: { id: 'other', label: 'Advanced', icon: Gear },
  people: {
    id: 'people',
    label: <div className="flex items-center gap-2">People</div>,
    icon: People,
  },
  smartTags: { id: 'smartTags', label: 'Smart Tags', icon: Ai },
  source: { id: 'source', label: 'Upload Source', icon: Import },
  state: { id: 'state', label: 'State/Province', icon: Location },
  tags: { id: 'tags', label: 'Tags', icon: Tag },
  type: { id: 'type', label: 'Type', icon: FolderOpen },
  uploader: { id: 'uploader', label: 'Uploaded By', icon: User },
  videoAspectRatio: { id: 'videoAspectRatio', label: 'Signal Aspect Ratio', icon: Video },
  videoFrameRate: { id: 'videoFrameRate', label: 'Video Frame Rate', icon: Video },
  locations: { id: 'locations', label: 'Locations', icon: LocationsIcon },
  advanced: { id: 'advanced', label: 'EXIF & IPTC', icon: Camera },
  separator1: { id: 'separator1', label: '', icon: Air },
  separator2: { id: 'separator2', label: '', icon: Air },
  assetFileKind: { id: 'assetFileKind', label: 'Asset File Kind', icon: FolderOpen },
  colorThreshold: { id: 'colorThreshold', label: 'Color Threshold', icon: Palette },
  similarToImage: { id: 'similarToImage', label: 'Similar To Image', icon: ImageSearchIcon },
  similarTo: { id: 'similarTo', label: 'Similar To', icon: ImageSearchIcon },
};

export type FilterSelectType = (filter: FilterType, customField?: CustomFieldFilterType) => void;

export type GetFiltersMenuOptionsParams = {
  onSelect: FilterSelectType;
  customFields: CustomFieldFilterType[];
  selectedFilters?: SelectedFilter[];
  availableFilters: FilterType[];
  disabledFilters?: FilterType[];
};
